/**
 * V5 workbench "Control surfaces" mode — CAD in, aileron out.
 *
 * The user-facing fold of the control-surface injection pipeline:
 *   1. SOURCE   — click a backend-listed example geometry (one gesture: fills the
 *                 step path + segments), or point at an assembly id (from a prior
 *                 geometry upload) / a STEP path (dev convenience). The Aircraft
 *                 screen can also seed the source via csSourceSeed (one-shot
 *                 sessionStorage, consumed on mount).
 *   2. SEGMENT  — POST /geometry/segment labels the lifting surfaces (wing,
 *                 htail, vtail) as bboxes; pick one and its spanwise airfoil
 *                 sections are extracted (POST /geometry/sections, n=21).
 *   3. DESIGN   — eta band, hinge x/c, cove gap/fillet, pivot height and
 *                 deflection drive the IN-BROWSER cove engine (computeCsShape)
 *                 on every debounced change: live 2D station preview, slot
 *                 health chips, and the hinge line on the planform schematic.
 *   4. PERSIST  — named variants (thread_core nodes) save/load the full param
 *                 record; "Solve this section" seeds the High-Lift workbench
 *                 with the deployed section; "Bake true cut" asks the backend
 *                 CAD engine for the real STEP cut (honest note when the
 *                 engine is not enabled in the environment).
 *
 * Left pane: a Planform | 3D toggle (planform default). Planform is a schematic
 * drawn from the segmented bboxes (x vs span, x vs z for the fin) with the
 * active CS band shaded and the engine hinge line at the band edges. 3D shows
 * the tessellated aircraft (POST /geometry/tessellate, cached per source) with
 * the control-surface silhouette lofted CLIENT-SIDE from the live cs0 contours
 * at LOFT_STATIONS etas across the band - it re-lofts on the same 30ms engine
 * debounce and pivots about the 3D hinge line by the deflection slider. The
 * mirror checkbox stays planform-only for now (the mirrored deflection sign is
 * CS-type-dependent - aileron vs flap - so the 3D pane does not guess).
 *
 * BAKED mode: the cut route is job-shaped (POST /control-surface/cut returns
 * a handle; pollCutJob drives GET /cut/{job_id} to completion, ~10s/control).
 * A complete job carries the baked STEP paths on disk - those tessellate
 * through the SAME /geometry/tessellate sidecar cache (keyed step:{path}, so
 * toggling never re-tessellates) and a Source | Baked pill next to the view
 * toggle swaps the 3D pane onto the REAL cut mesh. Baked mode hides the
 * parametric loft overlay + hinge (the cut already contains the surface at
 * its baked deflection) but keeps the upSign roll. Multiple baked outputs
 * (mirror expansion, several deflections) land in a small selector on the
 * baked chip; baked is auto-selected right after a successful bake. Baked
 * state resets when the SOURCE changes (a new segment run).
 *
 * Sections come back in the CAD's MODEL units, with the /sections response
 * reporting unit_mm (millimetres per model unit - 1 for a mm-authored STEP,
 * 1000 for metres, 304.8 for feet). The mm gap/fillet sliders are divided by
 * unit_mm before feeding computeCsShape - the engine contract takes gap and
 * fillet in section units - while the bake route keeps receiving true mm
 * (the backend converts).
 *
 * STEP z-conventions vary (the C172 example is authored z-DOWN; the Do228 is
 * z-up) and nothing in the file declares it, so /segment infers an aircraft-
 * level up_sign (fin direction, else a wing camber cue). It is DISPLAY-ONLY:
 * the 3D pane rolls its content group upright, the planform flips the fin
 * pane's drawn z, and the 2D section mirrors its vert axis - while the engine,
 * bake and stored/seeded SECTION coords all stay in the model frame. The
 * "Solve this section" seed additionally carries the CLEAN (uncut) station as
 * a normalized base foil (cleanFoil - unit chord, LE at 0, aero-up via
 * up_sign) so the high-lift workbench starts from the aircraft's own section.
 *
 * nTop MODE (FXC-9009): picking an "nTop parametric" chip (a partner .ntop
 * notebook with declared scalar inputs + optionally a PRE-BAKED frame set -
 * one STL per deflection value) enters a SIBLING mode that bypasses the
 * segment/sections pipeline entirely. The left pane is the 3D viewer only;
 * every frame is PRELOADED into the shared tess cache so scrubbing the
 * deflection slider swaps meshes near-instantly, and fitKey pins the camera
 * per NOTEBOOK so the swap never refits the view. The right panel is
 * NtopParametricPanel (inputs / per-frame mesh QA / nTopCL bake / 2D slice
 * into the High-Lift path). Picking any other source - or Reset - exits back
 * to the normal pipeline (runSegment clears ntopKey).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

import {
  cancelCutJob,
  fetchSections,
  listVariants,
  saveVariant,
  segmentAircraft,
  tessellateGeometry,
  type CsVariantRecord,
  type ExampleGeometry,
  type SegSurface,
} from "../../api/controlSurface";
import { exportAircraftDesignStep, type AircraftDesignSummary } from "../../api/aircraft";
import { cancelNtopBakeJob, type NtopNotebook } from "../../api/ntop";
import {
  computeCsShape,
  contourToPoints,
  interpolateSection,
  type CsPoint,
  type CsSections,
  type CsShapeResult,
} from "../highlift/geometry/csEngine";
import { toSlottedSection, type SlottedSection } from "../highlift/geometry/slottedSection";
import { normalizeCleanFoil, publishSectionSeed, type CleanFoilSeed } from "../highlift/foil/sectionSeed";
import { useHighliftTheme } from "../highlift/foil/useHighliftTheme";
import { bakedOutputLabel } from "./controlSurfaceBake";
import {
  useCsSession,
  useCsSessionStore,
  patchCsSession,
  startBake,
  startNtopBake,
  type CsParams,
  type CsSession,
} from "./csSessionStore";
import { loadCsSources, type CsSource } from "./csSources";
import { bakeAxisValues, framesAxisInput, frameValues, nearestFrame } from "./ntopFrames";
import { makeControlSurfaceStyles, ParamSlider } from "./ControlSurfaceControls";
import { ControlSurfaceExamples } from "./ControlSurfaceExamples";
import { NtopParametricPanel } from "./NtopParametricPanel";
import { ControlSurfacePlanform, spansBothSides } from "./ControlSurfacePlanform";
import { ControlSurfaceSectionPanel } from "./ControlSurfaceSectionPanel";
import { ControlSurfaceViewer3D, type CsLoft, type CsLoftRing } from "./ControlSurfaceViewer3D";
import { consumeSourceSeed } from "./csSourceSeed";
import type { SurfaceProps } from "./ThreadV5Surfaces";

// ---------------------------------------------------------------------------
// Small helpers (the param model + status unions now live in csSessionStore)
// ---------------------------------------------------------------------------

const CS_TYPES = ["aileron", "elevator", "rudder", "flap", "elevon", "flaperon"];

/** Stable identity for a segmented surface (labels can repeat across semispans). */
function surfaceKey(s: SegSurface): string {
  return `${s.label}:${s.bbox.join(",")}`;
}

/** Default CS type from the segmenter's surface label (editable afterwards). */
function inferCsType(label: string): string {
  const l = label.toLowerCase();
  if (/(vtail|fin|vert|rudder)/.test(l)) return "rudder";
  if (/(htail|stab|horiz|elev|tail|canard)/.test(l)) return "elevator";
  return "aileron";
}

/** SlottedSection surface kind from the segmenter label (for the high-lift seed). */
function surfaceKind(label: string): SlottedSection["surface"] {
  const l = label.toLowerCase();
  if (/canard/.test(l)) return "canard";
  if (/(vtail|fin|vert|rudder)/.test(l)) return "vtail";
  if (/(htail|stab|horiz|elev|tail)/.test(l)) return "htail";
  return "wing";
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

const num = (x: unknown, fallback: number): number =>
  typeof x === "number" && Number.isFinite(x) ? x : fallback;

// ---------------------------------------------------------------------------
// 3D silhouette loft (rings + hinge for ControlSurfaceViewer3D)
// ---------------------------------------------------------------------------

/** Stations lofted across the CS band for the 3D silhouette (incl. both edges). */
const LOFT_STATIONS = 7;
/** Points per silhouette ring after arclength resampling. */
const LOFT_RING_POINTS = 64;

/** Resample a polyline to n points by cumulative arclength (the studio's resampleClosed). */
function resampleByArclength(poly: CsPoint[], n: number): CsPoint[] {
  const cum = [0];
  for (let i = 1; i < poly.length; i++) {
    const prev = poly[i - 1]!;
    const cur = poly[i]!;
    cum.push(cum[i - 1]! + Math.hypot(cur[0] - prev[0], cur[1] - prev[1]));
  }
  const total = cum[cum.length - 1]! || 1;
  const out: CsPoint[] = [];
  for (let k = 0; k < n; k++) {
    const s = (k / (n - 1)) * total;
    let j = 1;
    while (j < cum.length && cum[j]! < s) j++;
    if (j >= cum.length) j = cum.length - 1;
    const p0 = poly[j - 1]!;
    const p1 = poly[j]!;
    const t = (s - cum[j - 1]!) / (cum[j]! - cum[j - 1]! || 1);
    out.push([p0[0] + t * (p1[0] - p0[0]), p0[1] + t * (p1[1] - p0[1])]);
  }
  return out;
}

/** Map a section-plane point (x, vert) at span station `span` into 3D by span axis:
 *  'y' -> [x, span, vert] (wing/tails), 'z' -> [x, vert, span] (the fin). */
function mapPt(
  spanAxis: "y" | "z",
  x: number,
  span: number,
  vert: number,
): [number, number, number] {
  return spanAxis === "y" ? [x, span, vert] : [x, vert, span];
}

/**
 * Build the 3D silhouette input: cs0 contour rings at LOFT_STATIONS etas across
 * the band, plus the band-edge hinge endpoints from the a/b engine shapes. Eta
 * runs across the FULL bbox span - the engine / sections / bake convention.
 * Null when a band edge has no valid engine section (the hinge leaves the
 * airfoil, so there is no honest hinge line to pivot about).
 */
function buildCsLoft(
  sections: CsSections,
  surface: SegSurface,
  d: { eta1: number; eta2: number; xc: number; yh: number; gap: number; fillet: number },
  a: CsShapeResult | null,
  b: CsShapeResult | null,
): CsLoft | null {
  const lo = surface.span_axis === "y" ? surface.bbox[1] : surface.bbox[2];
  const hi = surface.span_axis === "y" ? surface.bbox[4] : surface.bbox[5];
  const spanAt = (eta: number) => lo + eta * (hi - lo);
  // Loft from every station that computes - a band EDGE can honestly fail (e.g. the
  // fuselage carry-through has no clean airfoil), and requiring both edges would kill
  // the whole silhouette for a band that merely touches that zone. The hinge line is
  // taken from the first/last VALID stations instead of strictly the band edges.
  const rings: CsLoftRing[] = [];
  const hingeStations: Array<{ shape: CsShapeResult; span: number }> = [];
  for (let i = 0; i < LOFT_STATIONS; i++) {
    const f = i / (LOFT_STATIONS - 1);
    const shape =
      i === 0
        ? a
        : i === LOFT_STATIONS - 1
          ? b
          : computeCsShape(sections, d.eta1, d.eta2, d.xc, d.yh, d.gap, 0, f, d.fillet);
    if (!shape || shape.cs_contour_0.x.length < 4) continue; // skip degenerate stations
    const span = spanAt(d.eta1 + f * (d.eta2 - d.eta1));
    hingeStations.push({ shape, span });
    rings.push(
      resampleByArclength(contourToPoints(shape.cs_contour_0), LOFT_RING_POINTS).map(([x, v]) =>
        mapPt(surface.span_axis, x, span, v),
      ),
    );
  }
  if (rings.length < 2) return null;
  const h0 = hingeStations[0]!;
  const h1 = hingeStations[hingeStations.length - 1]!;
  return {
    rings,
    hinge: [
      mapPt(surface.span_axis, h0.shape.hinge_x, h0.span, h0.shape.hinge_z),
      mapPt(surface.span_axis, h1.shape.hinge_x, h1.span, h1.shape.hinge_z),
    ],
    spanAxis: surface.span_axis,
  };
}

// ---------------------------------------------------------------------------
// Surface
// ---------------------------------------------------------------------------

export function ControlSurfacesSurface(props: SurfaceProps) {
  const { projectId, branch, persistedProject, onOpenView } = props;
  const palette = useHighliftTheme();

  // ---- Session state (survives view switches; keyed projectId::branch) ------
  // The V5 shell unmounts this surface on every view switch; lifting the whole
  // session into a keyed store (mirroring the Study Cursor) keeps segmentation,
  // the selected surface, sections, params, the view and the baked cut alive.
  const { session, patchSession, sessionKey } = useCsSession(projectId, branch);
  const {
    assemblyId,
    stepPath,
    examples,
    examplesError,
    designs,
    designsError,
    segState,
    surfaces,
    selected,
    upSign,
    sectionsCache,
    params,
    csName,
    csType,
    sectionEta,
    viewMode,
    tessCache,
    stlUnits,
    bake,
    bakedOutputs,
    bakedErrors,
    bakedIndex,
    meshSource,
    ntopStatus,
    ntopError,
    ntopKey,
    ntopFrameValue,
    ntopBake,
    ntopSliceEta,
  } = session;

  // Simple field setters (patch the session; a view switch no longer loses them).
  const setAssemblyId = useCallback((v: string) => patchSession({ assemblyId: v }), [patchSession]);
  const setStepPath = useCallback((v: string) => patchSession({ stepPath: v }), [patchSession]);
  const setViewMode = useCallback((v: "planform" | "3d") => patchSession({ viewMode: v }), [patchSession]);
  const setMeshSource = useCallback((v: "source" | "baked") => patchSession({ meshSource: v }), [patchSession]);
  const setBakedIndex = useCallback((v: number) => patchSession({ bakedIndex: v }), [patchSession]);
  const setSectionEta = useCallback((v: number) => patchSession({ sectionEta: v }), [patchSession]);
  const setCsName = useCallback((v: string) => patchSession({ csName: v }), [patchSession]);
  const setCsType = useCallback((v: string) => patchSession({ csType: v }), [patchSession]);
  const setStlUnits = useCallback(
    (v: "auto" | "mm" | "ft" | "m") => patchSession({ stlUnits: v }),
    [patchSession],
  );

  // Updater-form setters read the LIVE session from the store, so async callbacks
  // (fetch .then, prefetch chain) merge onto the latest cache, not a stale closure.
  const setParams = useCallback(
    (updater: CsParams | ((p: CsParams) => CsParams)) => {
      const cur = useCsSessionStore.getState().byKey[sessionKey];
      if (!cur) return;
      const next = typeof updater === "function" ? updater(cur.params) : updater;
      patchCsSession(sessionKey, { params: next });
    },
    [sessionKey],
  );
  const setSectionsCache = useCallback(
    (updater: (cache: CsSession["sectionsCache"]) => CsSession["sectionsCache"]) => {
      const cur = useCsSessionStore.getState().byKey[sessionKey];
      if (!cur) return;
      patchCsSession(sessionKey, { sectionsCache: updater(cur.sectionsCache) });
    },
    [sessionKey],
  );
  const setTessCache = useCallback(
    (updater: (cache: CsSession["tessCache"]) => CsSession["tessCache"]) => {
      const cur = useCsSessionStore.getState().byKey[sessionKey];
      if (!cur) return;
      patchCsSession(sessionKey, { tessCache: updater(cur.tessCache) });
    },
    [sessionKey],
  );

  // ---- Transient / derived LOCAL state (rebuilt cheaply on a remount) -------
  // Engine results: mid = at the scrubber; a/b = band edges (for the hinge line).
  const [shapes, setShapes] = useState<{
    mid: CsShapeResult | null;
    a: CsShapeResult | null;
    b: CsShapeResult | null;
  } | null>(null);
  // Silhouette rings + hinge for the 3D pane - rebuilt on the engine debounce.
  const [loft, setLoft] = useState<CsLoft | null>(null);
  // Sections/tess in-flight + parked-error markers (a remount costs one retry).
  const [sectionsLoading, setSectionsLoading] = useState<string | null>(null);
  const [sectionsError, setSectionsError] = useState<string | null>(null);
  const [tessLoading, setTessLoading] = useState<string | null>(null);
  const [tessError, setTessError] = useState<{ key: string; message: string } | null>(null);
  // Variants + toasts (variants refetched per project; notes are ephemeral).
  const [variants, setVariants] = useState<CsVariantRecord[]>([]);
  const [variantNote, setVariantNote] = useState<string | null>(null);
  const [solveNote, setSolveNote] = useState<string | null>(null);
  // Design export busy state (the picked design exporting to a STEP) + its note.
  const [exportingDesignId, setExportingDesignId] = useState<string | null>(null);
  const [designNote, setDesignNote] = useState<string | null>(null);

  const cacheEntry = selected ? sectionsCache[surfaceKey(selected)] ?? null : null;
  const sections = cacheEntry?.sections ?? null;
  // Tessellation source identity: assembly id wins (the backend resolves it
  // first), then the step path - the same precedence /segment applies.
  const sourceKey = useMemo(() => {
    const asm = assemblyId.trim();
    if (asm) return `asm:${asm}`;
    const step = stepPath.trim();
    return step ? `step:${step}` : null;
  }, [assemblyId, stepPath]);
  // The 3D pane's ACTIVE mesh target: the source aircraft, or (in baked mode)
  // one of the baked cut STEPs. Baked entries share the same per-key tess
  // cache (step:{path} - content-hashed server-side too), so toggling
  // Source | Baked or re-picking an output never re-tessellates.
  const activeBaked = meshSource === "baked" ? bakedOutputs[bakedIndex] ?? null : null;
  const tessKey = activeBaked ? `step:${activeBaked.stepPath}` : sourceKey;
  const isStlSource = stepPath.trim().toLowerCase().endsWith(".stl");
  /** Millimetres per section/model unit - divides the mm sliders for the engine. */
  const unitMm = useMemo(() => {
    const reported = cacheEntry?.unitMm ?? 1;
    if (!isStlSource) return reported;
    if (stlUnits === "mm") return 1;
    if (stlUnits === "ft") return 304.8;
    if (stlUnits === "m") return 1000;
    // auto: an aircraft under 500 units across is not millimetres - feet is the VSP default.
    if (selected) {
      const ext = Math.max(
        selected.bbox[3] - selected.bbox[0],
        selected.bbox[4] - selected.bbox[1],
        selected.bbox[5] - selected.bbox[2],
      );
      if (ext > 0 && ext < 500) return 304.8;
    }
    return reported;
  }, [cacheEntry, isStlSource, selected, stlUnits]);
  const mirrorable = selected ? spansBothSides(selected) : false;

  // ---- nTop mode derived state (FXC-9009) ------------------------------------
  // A picked notebook key resolves against the aggregated ntop status; a key
  // whose notebook vanished (status refetch) simply falls back to the pipeline.
  const ntopNotebook = useMemo(
    () => (ntopKey ? ntopStatus?.notebooks.find((n) => n.key === ntopKey) ?? null : null),
    [ntopKey, ntopStatus],
  );
  const ntopMode = ntopNotebook !== null;
  const ntopFrameList = useMemo(() => ntopNotebook?.frames?.frames ?? [], [ntopNotebook]);
  // The ACTIVE frame: the manifest entry nearest the session's axis value (the
  // slider snaps writes, but a fresh manifest can shift the grid under it).
  const activeNtopFrame = useMemo(() => {
    if (ntopFrameList.length === 0) return null;
    const v = nearestFrame(ntopFrameList.map((f) => f.value), ntopFrameValue);
    return ntopFrameList.find((f) => f.value === v) ?? ntopFrameList[0] ?? null;
  }, [ntopFrameList, ntopFrameValue]);
  // Per-frame tessellation failures, parked locally so a bad STL cannot loop
  // the preload chain (a source pick or remount starts a fresh retry pass).
  const [ntopFrameErrors, setNtopFrameErrors] = useState<Record<string, string>>({});
  const ntopFramesLoaded = useMemo(
    () => ntopFrameList.reduce((n, f) => (tessCache[`step:${f.stl}`] ? n + 1 : n), 0),
    [ntopFrameList, tessCache],
  );
  const ntopActiveMesh = activeNtopFrame ? tessCache[`step:${activeNtopFrame.stl}`] ?? null : null;

  const refreshVariants = useCallback(() => {
    if (!persistedProject) return;
    listVariants(projectId, branch)
      .then((res) => setVariants(res.variants))
      .catch(() => setVariants([]));
  }, [branch, persistedProject, projectId]);

  useEffect(() => {
    refreshVariants();
  }, [refreshVariants]);

  // Debounced (~30ms) engine run on every param change - serverless live 2D,
  // plus (in 3D mode) the silhouette loft rebuilt on the SAME debounce tick.
  useEffect(() => {
    if (!sections || Object.keys(sections).length === 0) {
      setShapes(null);
      setLoft(null);
      return;
    }
    const t = window.setTimeout(() => {
      const { eta1, eta2, xc, yh, gapMm, filletMm, deflectionDeg } = params;
      // The engine takes gap/fillet in SECTION units; the sliders are true mm.
      const gap = gapMm / unitMm;
      const fillet = filletMm / unitMm;
      try {
        const a = computeCsShape(sections, eta1, eta2, xc, yh, gap, 0, 0, fillet);
        const b = computeCsShape(sections, eta1, eta2, xc, yh, gap, 0, 1, fillet);
        setShapes({
          mid: computeCsShape(sections, eta1, eta2, xc, yh, gap, deflectionDeg, sectionEta, fillet),
          a,
          b,
        });
        // The loft costs a handful of extra engine runs - only pay it when the
        // 3D pane is actually showing. Toggling to 3D re-runs this effect.
        setLoft(
          viewMode === "3d" && selected
            ? buildCsLoft(sections, selected, { eta1, eta2, xc, yh, gap, fillet }, a, b)
            : null,
        );
      } catch {
        // Malformed sections from the backend (empty or ragged arrays) must not
        // take the surface down with an uncaught error - show the honest null state.
        setShapes(null);
        setLoft(null);
      }
    }, 30);
    return () => window.clearTimeout(t);
  }, [params, sectionEta, sections, selected, unitMm, viewMode]);

  // Tessellate fetch: the 3D pane being VISIBLE (3D mode + a segmented source -
  // the viewer only renders once surfaces exist) pulls the display mesh for the
  // ACTIVE target (source aircraft, or the selected baked cut STEP), cached per
  // key. Gating on surfaces also stops a request per keystroke while a step
  // path is being typed in 3D mode, and sequences tess after segmentation
  // instead of contending for the backend's OCC slot. A parked error stops the
  // re-fetch until Retry clears it.
  useEffect(() => {
    // ntop mode has its own preload chain below - the pipeline's source mesh
    // is not showing, so fetching it here would only contend for the backend.
    if (ntopMode || viewMode !== "3d" || !tessKey || surfaces.length === 0) return;
    if (tessCache[tessKey] || tessError?.key === tessKey) return;
    let cancelled = false;
    setTessLoading(tessKey);
    tessellateGeometry(
      projectId,
      activeBaked
        ? { stepPath: activeBaked.stepPath }
        : {
            assemblyId: assemblyId.trim() || undefined,
            stepPath: stepPath.trim() || undefined,
          },
    )
      .then((res) => {
        // Cache even when cancelled (toggled back to planform mid-flight): the
        // mesh is keyed by target and stays valid, so the next 3D entry is
        // instant instead of re-running a multi-second tessellation.
        setTessCache((cache) => ({ ...cache, [tessKey]: res.mesh }));
      })
      .catch((e) => {
        if (!cancelled) setTessError({ key: tessKey, message: errorMessage(e) });
      })
      .finally(() => setTessLoading((current) => (current === tessKey ? null : current)));
    return () => {
      cancelled = true;
    };
  }, [activeBaked, assemblyId, ntopMode, projectId, stepPath, surfaces.length, tessCache, tessError, tessKey, viewMode]);

  // nTop frame PRELOAD: pull EVERY frame's display mesh into the SHARED tess
  // cache (step:{ref} keys - the same cache the baked toggle uses; sidecar-
  // cached server-side too), ACTIVE frame first, then one un-cached frame per
  // pass - the cache merge re-runs the effect, the same sequential chain as
  // the sections prefetch. Preloading is what makes scrubbing near-instant:
  // a slider tick is a cache lookup, never a fetch.
  useEffect(() => {
    if (!ntopMode || ntopFrameList.length === 0) return;
    const queue = activeNtopFrame ? [activeNtopFrame, ...ntopFrameList] : ntopFrameList;
    const next = queue.find((f) => !tessCache[`step:${f.stl}`] && !ntopFrameErrors[f.stl]);
    if (!next) return;
    const key = `step:${next.stl}`;
    let cancelled = false;
    tessellateGeometry(projectId, { stepPath: next.stl })
      .then((res) => {
        // Cache even when cancelled - the mesh is keyed by frame ref and stays
        // valid, so re-entering ntop mode never re-tessellates.
        setTessCache((cache) => (cache[key] ? cache : { ...cache, [key]: res.mesh }));
      })
      .catch((e) => {
        if (!cancelled) setNtopFrameErrors((m) => ({ ...m, [next.stl]: errorMessage(e) }));
      });
    return () => {
      cancelled = true;
    };
  }, [activeNtopFrame, ntopFrameErrors, ntopFrameList, ntopMode, projectId, tessCache]);

  const loadSections = useCallback(
    (surface: SegSurface) => {
      const key = surfaceKey(surface);
      setSectionsError(null);
      setSectionsLoading(key);
      fetchSections(projectId, {
        assemblyId: assemblyId.trim() || undefined,
        stepPath: stepPath.trim() || undefined,
        surface,
        n: 21,
      })
        .then((res) => {
          // Default unit_mm=1 (mm-authored STEP / older server); guard non-positive junk.
          const unit = num(res.unit_mm, 1);
          setSectionsCache((cache) => ({
            ...cache,
            [key]: { sections: res.sections, unitMm: unit > 0 ? unit : 1 },
          }));
        })
        .catch((e) => setSectionsError(errorMessage(e)))
        .finally(() => setSectionsLoading((current) => (current === key ? null : current)));
    },
    [assemblyId, projectId, stepPath],
  );

  const pickSurface = useCallback(
    (surface: SegSurface) => {
      const inferred = inferCsType(surface.label);
      patchSession({
        selected: surface,
        csType: inferred,
        csName: `${inferred}-${surface.label}`.toLowerCase(),
        bake: { status: "idle" },
      });
      setParams((p) => ({ ...p, mirror: spansBothSides(surface) }));
      setSolveNote(null);
      if (!sectionsCache[surfaceKey(surface)]) loadSections(surface);
    },
    [loadSections, patchSession, sectionsCache, setParams],
  );

  // Quiet background prefetch: one un-cached surface per pass (the cache merge re-runs
  // the effect, giving a natural sequential chain - the OCC slicer is serialized
  // server-side anyway). This is what upgrades the planform from bounding boxes to
  // TRUE outlines; failures stay silent and that surface simply keeps its box.
  useEffect(() => {
    if (segState.status !== "done" || surfaces.length === 0) return;
    const next = surfaces.find((s) => !sectionsCache[surfaceKey(s)]);
    if (!next) return;
    const key = surfaceKey(next);
    if (sectionsLoading === key) return; // the selected-surface fetch already has it
    let cancelled = false;
    fetchSections(projectId, {
      assemblyId: assemblyId.trim() || undefined,
      stepPath: stepPath.trim() || undefined,
      surface: next,
      n: 21,
    })
      .then((res) => {
        if (cancelled) return;
        const unit = num(res.unit_mm, 1);
        setSectionsCache((cache) =>
          cache[key] ? cache : { ...cache, [key]: { sections: res.sections, unitMm: unit > 0 ? unit : 1 } },
        );
      })
      .catch(() => {
        /* quiet - the planform keeps the bbox for this surface */
      });
    return () => {
      cancelled = true;
    };
  }, [assemblyId, projectId, sectionsCache, sectionsLoading, segState.status, stepPath, surfaces]);

  // True planform outlines - (span, x_le, x_te) per station - for every surface whose
  // sections have arrived. Keyed by surfaceKey; the planform falls back to the bbox
  // for anything missing.
  const planformOutlines = useMemo(() => {
    const out: Record<string, Array<[number, number, number]>> = {};
    for (const s of surfaces) {
      const entry = sectionsCache[surfaceKey(s)];
      if (!entry) continue;
      const pts = Object.values(entry.sections)
        .map((sec) => [sec.y, sec.x_le, sec.x_te] as [number, number, number])
        .filter((p) => p.every(Number.isFinite))
        .sort((a, b) => a[0] - b[0]);
      if (pts.length >= 2) out[surfaceKey(s)] = pts;
    }
    return out;
  }, [sectionsCache, surfaces]);

  // Core segment run with an EXPLICIT source - the example chips and the mount
  // seed call this with their own path (state writes have not landed yet there).
  const runSegment = useCallback(
    async (source: { assemblyId?: string; stepPath?: string }) => {
      const cur = useCsSessionStore.getState().byKey[sessionKey];
      // A new source resets segmentation and invalidates any baked cuts; bumping
      // bakeGen cancels an in-flight bake poll (its results would belong to the
      // OLD aircraft), and it survives across an unmount now that it is in the store.
      const gen = (cur?.bakeGen ?? 0) + 1;
      patchCsSession(sessionKey, {
        segState: { status: "running" },
        surfaces: [],
        selected: null,
        upSign: 1,
        bakeGen: gen,
        bakedOutputs: [],
        bakedErrors: [],
        bakedIndex: 0,
        meshSource: "source",
        bake: { status: "idle" },
        // Any segment run means the NORMAL pipeline - exit ntop mode (picking
        // an example/design/session source is the documented way back out).
        ntopKey: "",
        ntopBake: { status: "idle" },
      });
      setNtopFrameErrors({});
      setShapes(null);
      try {
        const res = await segmentAircraft(projectId, source);
        patchCsSession(sessionKey, {
          surfaces: res.surfaces,
          // Aircraft-level z-orientation rides the segment response (absent on older servers -> +1).
          upSign: res.up_sign === -1 ? -1 : 1,
          segState: { status: "done", cached: Boolean(res.cached) },
        });
      } catch (e) {
        patchCsSession(sessionKey, { segState: { status: "error", error: errorMessage(e) } });
      }
    },
    [projectId, sessionKey],
  );

  const handleSegment = useCallback(() => {
    void runSegment({
      assemblyId: assemblyId.trim() || undefined,
      stepPath: stepPath.trim() || undefined,
    });
  }, [assemblyId, runSegment, stepPath]);

  // Example chip: one gesture - fill the step-path input and segment that file.
  // The backend resolves assembly_id FIRST, so the assembly input is cleared too
  // (clicking an example is an explicit "use this file").
  const pickExample = useCallback(
    (example: ExampleGeometry) => {
      setAssemblyId("");
      setStepPath(example.path);
      void runSegment({ stepPath: example.path });
    },
    [runSegment],
  );

  // One-shot source seed from the Aircraft screen (csSourceSeed): consume on the
  // FIRST mount pass, set the step path, and - when a path came through - segment
  // immediately. Gated on session-emptiness: a REMOUNT over a live session must
  // never consume/clobber it (the store already holds the durable copy).
  const seedConsumed = useRef(false);
  useEffect(() => {
    if (seedConsumed.current) return;
    seedConsumed.current = true;
    const cur = useCsSessionStore.getState().byKey[sessionKey];
    if (cur && (cur.stepPath || cur.assemblyId || cur.surfaces.length > 0)) return;
    const seed = consumeSourceSeed();
    if (!seed?.stepPath) return;
    patchCsSession(sessionKey, { stepPath: seed.stepPath });
    if (persistedProject) void runSegment({ stepPath: seed.stepPath });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only (ref-guarded)
  }, []);

  // Aggregate the source picker (examples + project designs) once per session.
  // Gated on examplesLoaded so a remount does not refetch or flash - the store
  // already holds the aggregated groups. Quiet on failure per-group (allSettled).
  useEffect(() => {
    if (!persistedProject) return;
    const cur = useCsSessionStore.getState().byKey[sessionKey];
    if (cur?.examplesLoaded) return;
    let cancelled = false;
    loadCsSources(projectId)
      .then((res) => {
        if (cancelled) return;
        patchCsSession(sessionKey, {
          examples: res.examples,
          examplesError: res.examplesError,
          designs: res.designs,
          designsError: res.designsError,
          ntopStatus: res.ntop,
          ntopError: res.ntopError,
          examplesLoaded: true,
        });
      })
      .catch(() => {
        if (!cancelled) patchCsSession(sessionKey, { examplesLoaded: true });
      });
    return () => {
      cancelled = true;
    };
  }, [persistedProject, projectId, sessionKey]);

  // Project design pick: export the design to a STEP (slow - "Exporting..."), then
  // segment the returned geometry ref. Reuses runSegment like the example chips.
  const handleDesignPick = useCallback(
    async (design: AircraftDesignSummary) => {
      if (exportingDesignId) return;
      setDesignNote(null);
      setExportingDesignId(design.design_id);
      try {
        const res = await exportAircraftDesignStep(projectId, design.design_id);
        setAssemblyId("");
        setStepPath(res.step_path);
        await runSegment({ stepPath: res.step_path });
      } catch (e) {
        setDesignNote(`Export failed - ${errorMessage(e)}`);
      } finally {
        setExportingDesignId(null);
      }
    },
    [exportingDesignId, projectId, runSegment, setAssemblyId, setStepPath],
  );

  // nTop chip: enter ntop mode. NO segment call - the notebook's pre-baked
  // frame set drives the 3D pane directly and the segment/planform machinery
  // hides. The frame value seeds from the axis input's default, snapped onto
  // the baked grid so the viewer always shows a REAL frame.
  const pickNtop = useCallback(
    (nb: NtopNotebook) => {
      setNtopFrameErrors({});
      const values = frameValues(nb.frames);
      const axis = framesAxisInput(nb);
      const seed =
        typeof axis?.default === "number" && Number.isFinite(axis.default)
          ? axis.default
          : values[0] ?? 0;
      patchSession({
        ntopKey: nb.key,
        viewMode: "3d",
        meshSource: "source",
        ntopBake: { status: "idle" },
        ntopFrameValue: values.length > 0 ? nearestFrame(values, seed) : seed,
      });
    },
    [patchSession],
  );

  // Session (ephemeral) source pick: just re-segment its path / assembly id.
  const handleSessionPick = useCallback(
    (src: CsSource) => {
      if (src.kind !== "session") return;
      if (src.stepPath) {
        setAssemblyId("");
        setStepPath(src.stepPath);
        void runSegment({ stepPath: src.stepPath });
      } else if (src.assemblyId) {
        setStepPath("");
        setAssemblyId(src.assemblyId);
        void runSegment({ assemblyId: src.assemblyId });
      }
    },
    [runSegment, setAssemblyId, setStepPath],
  );

  // The tab-only sources: the current source when it is not one of the listed
  // examples (a seed handed from the Aircraft screen, or a manual entry).
  const sessionSources = useMemo<CsSource[]>(() => {
    const out: CsSource[] = [];
    const step = stepPath.trim();
    const asm = assemblyId.trim();
    if (step && !examples.some((e) => e.path === step)) {
      out.push({ kind: "session", label: step.split(/[\\/]/).pop() || step, stepPath: step, origin: "seed" });
    } else if (asm) {
      out.push({ kind: "session", label: `assembly ${asm.slice(0, 8)}`, assemblyId: asm, origin: "manual" });
    }
    return out;
  }, [assemblyId, examples, stepPath]);

  const setParam = useCallback((patch: Partial<CsParams>) => {
    setParams((p) => {
      const next = { ...p, ...patch };
      // Keep a real band: eta1 strictly below eta2 (matching the 0.02 slider step).
      if (next.eta1 > next.eta2 - 0.02) {
        if (patch.eta1 !== undefined) next.eta1 = Math.max(0, next.eta2 - 0.02);
        else next.eta2 = Math.min(1, next.eta1 + 0.02);
      }
      return next;
    });
  }, [setParams]);

  const handleSaveVariant = useCallback(async () => {
    if (!selected) return;
    setVariantNote(null);
    try {
      const res = await saveVariant(
        projectId,
        {
          name: csName.trim() || "unnamed",
          surface: selected.label,
          csType,
          params: {
            eta1: params.eta1,
            eta2: params.eta2,
            xc_hinge: params.xc,
            gap_mm: params.gapMm,
            fillet_mm: params.filletMm,
            yh: params.yh,
            deflection_deg: params.deflectionDeg,
            mirror: mirrorable ? params.mirror : false,
          },
        },
        branch,
      );
      setVariantNote(`Saved - ${res.nodePath}`);
      refreshVariants();
    } catch (e) {
      setVariantNote(`Save failed - ${errorMessage(e)}`);
    }
  }, [branch, csName, csType, mirrorable, params, projectId, refreshVariants, selected]);

  const loadVariant = useCallback(
    (v: CsVariantRecord) => {
      const cur = useCsSessionStore.getState().byKey[sessionKey];
      const base = cur?.params ?? params;
      const nextParams: CsParams = {
        eta1: num(v.params.eta1, base.eta1),
        eta2: num(v.params.eta2, base.eta2),
        xc: num(v.params.xc_hinge, base.xc),
        gapMm: num(v.params.gap_mm, base.gapMm),
        filletMm: num(v.params.fillet_mm, base.filletMm),
        yh: num(v.params.yh, base.yh),
        deflectionDeg: num(v.params.deflection_deg, base.deflectionDeg),
        mirror: typeof v.params.mirror === "boolean" ? v.params.mirror : base.mirror,
      };
      const patch: Partial<CsSession> = { csName: v.name, csType: v.csType, params: nextParams };
      const liveSurfaces = cur?.surfaces ?? surfaces;
      const liveSelected = cur?.selected ?? selected;
      const liveCache = cur?.sectionsCache ?? sectionsCache;
      const match = liveSurfaces.find((s) => s.label === v.surface);
      if (match && surfaceKey(match) !== (liveSelected ? surfaceKey(liveSelected) : null)) {
        patch.selected = match;
        if (!liveCache[surfaceKey(match)]) loadSections(match);
      }
      patchCsSession(sessionKey, patch);
      setVariantNote(`Loaded - ${v.name}`);
    },
    [loadSections, params, sectionsCache, selected, sessionKey, surfaces],
  );

  const handleSolve = useCallback(() => {
    if (!selected || !shapes?.mid) {
      setSolveNote("No valid section at this station - adjust the band or hinge first.");
      return;
    }
    const section = toSlottedSection(shapes.mid, {
      surface: surfaceKind(selected.label),
      csType,
      name: csName.trim() || csType,
      deflectionDeg: params.deflectionDeg,
      // Cut-rig provenance (SlottedSectionRig): feeds the cs_* axes on persisted runs
      // and the high-lift cove-depth mapping. gapMm/filletMm are TRUE mm (the sliders);
      // unitMm lets consumers convert them into the section's own model units.
      rig: {
        eta1: params.eta1,
        eta2: params.eta2,
        xc: params.xc,
        yh: params.yh,
        gapMm: params.gapMm,
        filletMm: params.filletMm,
        unitMm,
      },
    });
    // The CLEAN (uncut) section at the scrubber eta, normalized to the high-lift main-foil
    // ingest convention (Selig loop, unit chord, LE at 0, aero-up via upSign) - the high-lift
    // workbench sets it as the base foil and leaves the cove/flap design to its own tools.
    // Degenerate stations publish no cleanFoil; the intake then keeps its plain-flap fallback.
    let cleanFoil: CleanFoilSeed | undefined;
    if (sections) {
      const absEta = params.eta1 + sectionEta * (params.eta2 - params.eta1);
      const clean = interpolateSection(sections, absEta);
      const coords = normalizeCleanFoil(
        { upper: clean.upper, lower: clean.lower },
        clean.chord,
        upSign,
      );
      if (coords) {
        const stem =
          stepPath.trim().split(/[\\/]/).pop()?.replace(/\.[^.]+$/, "") ||
          assemblyId.trim() ||
          "aircraft";
        cleanFoil = { coords, name: `${stem} ${selected.label} eta ${absEta.toFixed(2)}` };
      }
    }
    publishSectionSeed({
      section,
      ...(cleanFoil ? { cleanFoil } : {}),
      note: `Seeded from Control surfaces - ${selected.label} band ${params.eta1.toFixed(2)}-${params.eta2.toFixed(2)} at ${params.deflectionDeg.toFixed(0)} deg`,
    });
    onOpenView("highlift");
  }, [assemblyId, csName, csType, onOpenView, params, sectionEta, sections, selected, shapes, stepPath, unitMm, upSign]);

  // Bake: hand off to the module-level startBake, which runs the whole poll and
  // writes results to the SESSION store - so an in-flight bake survives a view
  // switch and lands its baked outputs even after this surface has unmounted.
  const handleBake = useCallback(() => {
    const cur = useCsSessionStore.getState().byKey[sessionKey];
    if (!cur?.selected) return;
    const surface = cur.selected;
    void startBake(sessionKey, projectId, {
      assemblyId: cur.assemblyId.trim() || undefined,
      stepPath: cur.stepPath.trim() || undefined,
      controls: [
        {
          name: cur.csName.trim() || cur.csType,
          surface,
          eta1: cur.params.eta1,
          eta2: cur.params.eta2,
          xc_hinge: cur.params.xc,
          gap_mm: cur.params.gapMm,
          fillet_mm: cur.params.filletMm,
          yh: cur.params.yh,
          mirror: spansBothSides(surface) ? cur.params.mirror : false,
          deflections_deg: [cur.params.deflectionDeg],
        },
      ],
    });
  }, [projectId, sessionKey]);

  // Cancel a running cut: ask the backend to stop between controls (it cannot
  // kill a mid-cut OCC subprocess), and mark the note "Cancelling..." while the
  // same poll runs on to observe the terminal "cancelled" status.
  const handleCancelBake = useCallback(() => {
    const cur = useCsSessionStore.getState().byKey[sessionKey];
    const b = cur?.bake;
    if (!b || b.status !== "running") return;
    if (b.jobId) void cancelCutJob(projectId, b.jobId).catch(() => {});
    patchCsSession(sessionKey, {
      bake: { status: "running", message: "Cancelling...", jobId: b.jobId, progress: b.progress },
    });
  }, [projectId, sessionKey]);

  // nTop frame bake: sweep the AXIS input's declared range (bakeAxisValues
  // caps the sweep at 41 values) with the other scalars fixed at their
  // defaults. The poll lives in startNtopBake (module scope, generation-
  // guarded via bakeGen) so it survives a view switch, exactly like handleBake.
  const handleNtopBake = useCallback(() => {
    const cur = useCsSessionStore.getState().byKey[sessionKey];
    const nb = cur?.ntopStatus?.notebooks.find((n) => n.key === cur.ntopKey);
    if (!nb) return;
    const axis = framesAxisInput(nb);
    if (!axis) return;
    const values = bakeAxisValues(axis);
    if (values.length === 0) return;
    const fixed: Record<string, number> = {};
    for (const inp of nb.inputs) {
      if (inp.type !== "scalar" || inp.name === axis.name) continue;
      if (typeof inp.default === "number" && Number.isFinite(inp.default)) {
        fixed[inp.name] = inp.default;
      }
    }
    void startNtopBake(sessionKey, projectId, {
      notebookKey: nb.key,
      axis: axis.name,
      values,
      fixed,
    });
  }, [projectId, sessionKey]);

  // Cancel a running frame bake (checked between frames server-side, like the
  // cut cancel) - the same poll observes the terminal 'cancelled' status.
  const handleNtopCancelBake = useCallback(() => {
    const cur = useCsSessionStore.getState().byKey[sessionKey];
    const b = cur?.ntopBake;
    if (!b || b.status !== "running") return;
    if (b.jobId) void cancelNtopBakeJob(projectId, b.jobId).catch(() => {});
    patchCsSession(sessionKey, {
      ntopBake: { status: "running", message: "Cancelling...", jobId: b.jobId, progress: b.progress },
    });
  }, [projectId, sessionKey]);

  const S = useMemo(() => makeControlSurfaceStyles(palette), [palette]);
  const absoluteEta = selected ? params.eta1 + sectionEta * (params.eta2 - params.eta1) : null;
  const hingeEdges = shapes?.a && shapes?.b ? { x1: shapes.a.hinge_x, x2: shapes.b.hinge_x } : null;
  const sectionsPending = selected ? sectionsLoading === surfaceKey(selected) : false;
  const tessMesh = tessKey ? tessCache[tessKey] ?? null : null;
  const tessPending = tessKey !== null && tessLoading === tessKey;
  const tessFailure = tessKey && tessError?.key === tessKey ? tessError.message : null;

  // Bake contract guards (Task 4): the v1 shaped cut needs a STEP source and a
  // wing-like (spanwise-y) surface. Design + preview + solve stay available.
  const bakeBlockedReason = isStlSource
    ? "Baking needs a STEP source - STL can be designed and solved but not cut."
    : selected?.span_axis === "z"
      ? "The v1 cut path supports wing-like (spanwise-y) surfaces - fin cuts are not yet supported."
      : null;
  const bakeDisabled = bake.status === "running" || bakeBlockedReason !== null;
  const bakeProgressPct =
    bake.status === "running" && typeof bake.progress === "number"
      ? Math.round(Math.min(1, Math.max(0, bake.progress)) * 100)
      : null;

  /** Shared pill-button style for the viewer toggles. */
  const pillButton = (active: boolean): CSSProperties => ({
    background: active ? palette.primarySoft : "transparent",
    color: active ? palette.ink : palette.ink3,
    border: "none",
    borderRadius: 5,
    padding: "3px 10px",
    cursor: "pointer",
    font: "inherit",
    fontSize: 11.5,
    fontWeight: active ? 600 : 400,
  });

  /** Planform | 3D toggle pill (overlaid on the viewer pane). */
  const viewToggle = (mode: "planform" | "3d", label: string) => (
    <button
      key={mode}
      onClick={() => setViewMode(mode)}
      aria-pressed={viewMode === mode}
      style={pillButton(viewMode === mode)}
    >
      {label}
    </button>
  );

  /** Source | Baked mesh toggle (3D pane, only once a bake has landed). */
  const meshToggle = (mode: "source" | "baked", label: string) => (
    <button
      key={mode}
      onClick={() => setMeshSource(mode)}
      aria-pressed={meshSource === mode}
      style={pillButton(meshSource === mode)}
    >
      {label}
    </button>
  );

  /** Honest status chip for the 3D pane (tess loading / failure / no silhouette). */
  const viewerChip = (content: ReactNode) => (
    <div
      style={{
        position: "absolute",
        left: 10,
        bottom: 10,
        maxWidth: "70%",
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "5px 10px",
        borderRadius: 6,
        border: `1px solid ${palette.line}`,
        background: palette.surface2,
        color: palette.ink3,
        fontSize: 11,
        lineHeight: 1.4,
      }}
    >
      {content}
    </div>
  );

  if (!persistedProject) {
    return (
      <div style={S.root} data-testid="v5-control-surfaces-screen">
        <div style={S.toolbar}>
          <span style={S.title}>Control surfaces</span>
        </div>
        <div style={S.emptyState}>
          <h2 style={{ margin: 0, fontSize: 15 }}>No saved project selected</h2>
          <p style={{ margin: 0, maxWidth: 420, lineHeight: 1.5 }}>
            Segmentation, sections and cuts run against a saved Thread project. Open one from
            Projects, then come back to inject control surfaces into its CAD.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={S.root} data-testid="v5-control-surfaces-screen">
      <div style={S.toolbar}>
        <span style={S.title}>Control surfaces</span>
        <span style={S.subtitle}>CAD in - aileron out. Segment, design the cove live, bake the cut.</span>
        {ntopMode ? (
          <span style={S.stat} data-testid="cs-ntop-stat">
            nTop · {ntopFrameList.length} frame{ntopFrameList.length === 1 ? "" : "s"}
          </span>
        ) : segState.status === "done" ? (
          <span style={S.stat}>
            {surfaces.length} surface{surfaces.length === 1 ? "" : "s"}
            {segState.cached ? " · cached" : ""}
          </span>
        ) : null}
      </div>

      <div style={S.body}>
        <div style={S.viewer}>
          {ntopMode ? (
            /* nTop mode: the 3D viewer only - no planform, no segment overlays.
               fitKey pins the camera to the NOTEBOOK, so scrubbing frames swaps
               the mesh under a dead-still view (the demo's whole point). */
            <>
              <ControlSurfaceViewer3D
                palette={palette}
                mesh={ntopActiveMesh}
                loft={null}
                deflectionDeg={0}
                fitKey={ntopKey}
              />
              {ntopFrameList.length === 0
                ? viewerChip(
                    <span>
                      No baked frames for this notebook - bake with nTopCL in the panel, or
                      drop pre-baked frame STLs in the nTop dir.
                    </span>,
                  )
                : ntopFramesLoaded < ntopFrameList.length
                  ? viewerChip(
                      <span data-testid="cs-ntop-preload">
                        loading frames {ntopFramesLoaded}/{ntopFrameList.length}
                        {Object.keys(ntopFrameErrors).length > 0
                          ? ` - ${Object.keys(ntopFrameErrors).length} failed`
                          : ""}
                      </span>,
                    )
                  : !ntopActiveMesh
                    ? viewerChip(<span>Loading this frame...</span>)
                    : null}
            </>
          ) : surfaces.length > 0 ? (
            <>
              {viewMode === "planform" ? (
                <ControlSurfacePlanform
                  palette={palette}
                  surfaces={surfaces}
                  active={selected}
                  eta1={params.eta1}
                  eta2={params.eta2}
                  mirror={mirrorable && params.mirror}
                  hingeEdges={hingeEdges}
                  outlines={planformOutlines}
                  upSign={upSign}
                  onPick={pickSurface}
                />
              ) : (
                <>
                  <ControlSurfaceViewer3D
                    palette={palette}
                    mesh={tessMesh}
                    // Baked mode shows the REAL cut - the mesh already contains
                    // the surface at its baked deflection, so the parametric
                    // loft overlay + hinge would double-draw. upSign still rides
                    // the viewer's content group either way.
                    loft={activeBaked ? null : loft}
                    deflectionDeg={params.deflectionDeg}
                    upSign={upSign}
                  />
                  {tessPending
                    ? viewerChip(
                        <span>
                          {activeBaked
                            ? "Tessellating the baked cut..."
                            : "Tessellating the aircraft..."}
                        </span>,
                      )
                    : tessFailure
                      ? viewerChip(
                          <>
                            <span style={{ color: palette.warn }}>
                              Tessellation failed - {tessFailure}
                            </span>
                            <button
                              style={{ ...S.secondaryButton, padding: "2px 8px", fontSize: 11 }}
                              onClick={() => setTessError(null)}
                            >
                              Retry
                            </button>
                          </>,
                        )
                      : activeBaked
                        ? viewerChip(
                            <>
                              <span data-testid="cs-baked-label">
                                baked - {bakedOutputLabel(activeBaked)}
                              </span>
                              {bakedOutputs.length > 1 ? (
                                <select
                                  value={bakedIndex}
                                  onChange={(e) => setBakedIndex(Number(e.target.value))}
                                  aria-label="Baked output"
                                  style={{
                                    font: "inherit",
                                    fontSize: 11,
                                    background: palette.surface,
                                    color: palette.ink,
                                    border: `1px solid ${palette.line}`,
                                    borderRadius: 4,
                                    padding: "1px 4px",
                                  }}
                                >
                                  {bakedOutputs.map((o, i) => (
                                    <option key={o.stepPath} value={i}>
                                      {bakedOutputLabel(o)}
                                    </option>
                                  ))}
                                </select>
                              ) : null}
                              {bakedErrors.length > 0 ? (
                                <span
                                  data-testid="cs-baked-errors"
                                  style={{ color: palette.warn }}
                                >
                                  {bakedErrors.length} failed:{" "}
                                  {bakedErrors.map((e) => `${e.name}: ${e.error}`).join("; ")}
                                </span>
                              ) : null}
                            </>,
                          )
                        : bakedErrors.length > 0
                          ? viewerChip(
                              <span
                                data-testid="cs-baked-errors"
                                style={{ color: palette.warn }}
                              >
                                {bakedErrors.length} control(s) failed to bake:{" "}
                                {bakedErrors.map((e) => `${e.name}: ${e.error}`).join("; ")}
                              </span>,
                            )
                          : !selected
                          ? viewerChip(
                              <span>Pick a lifting surface to see its control surface here.</span>,
                            )
                          : !loft
                            ? viewerChip(
                                <span>
                                  No 3D silhouette - the engine has no valid section across this
                                  band. Pull the hinge x/c or the span band back onto the surface.
                                </span>,
                              )
                            : null}
                </>
              )}
              <div style={{ position: "absolute", top: 10, left: 10, display: "flex", gap: 8 }}>
                <div
                  style={{
                    display: "flex",
                    gap: 2,
                    padding: 2,
                    borderRadius: 7,
                    border: `1px solid ${palette.line}`,
                    background: palette.surface,
                  }}
                  role="group"
                  aria-label="Viewer mode"
                  data-testid="cs-view-toggle"
                >
                  {viewToggle("planform", "Planform")}
                  {viewToggle("3d", "3D")}
                </div>
                {viewMode === "3d" && bakedOutputs.length > 0 ? (
                  <div
                    style={{
                      display: "flex",
                      gap: 2,
                      padding: 2,
                      borderRadius: 7,
                      border: `1px solid ${palette.line}`,
                      background: palette.surface,
                    }}
                    role="group"
                    aria-label="Mesh source"
                    data-testid="cs-mesh-source-toggle"
                  >
                    {meshToggle("source", "Source")}
                    {meshToggle("baked", "Baked")}
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <div style={S.viewerEmpty}>
              {segState.status === "running"
                ? "Segmenting the aircraft..."
                : segState.status === "error"
                  ? `Segmentation failed - ${segState.error}`
                  : "Point at an assembly or STEP path, then Segment to see the lifting surfaces here."}
            </div>
          )}
        </div>

        <div style={S.panel}>
          {/* --- a. Source ------------------------------------------------- */}
          <div style={S.sectionHead}>Source</div>
          <div style={S.section}>
            <ControlSurfaceExamples
              examples={examples}
              ntopNotebooks={ntopStatus?.notebooks ?? []}
              ntopError={ntopError}
              designs={designs}
              designsError={designsError}
              sessionSources={sessionSources}
              // Lock the WHOLE picker during a segment run OR a design export:
              // an export runs its own runSegment on completion, so leaving the
              // example/session chips live would let a second pick race it and
              // desync stepPath from the segmented surfaces.
              busy={segState.status === "running" || exportingDesignId !== null}
              exportingDesignId={exportingDesignId}
              onPickExample={pickExample}
              onPickNtop={pickNtop}
              onPickDesign={handleDesignPick}
              onPickSession={handleSessionPick}
              palette={palette}
            />
            {examplesError ? (
              <span style={S.footNote}>Examples unavailable - {examplesError}</span>
            ) : null}
            {designNote ? <span style={S.footNote}>{designNote}</span> : null}
            {/* Raw source entry (assembly id / step path) - demoted to a dev drawer
                below the picker; the picker is the everyday path. */}
            <details style={{ marginTop: 2 }} data-testid="cs-source-advanced">
              <summary style={{ ...S.footNote, cursor: "pointer" }}>Advanced / dev source</summary>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 8 }}>
                <label style={S.fieldLabel}>
                  assembly id
                  <input
                    style={S.textInput}
                    value={assemblyId}
                    onChange={(e) => setAssemblyId(e.target.value)}
                    placeholder="from a geometry upload"
                    spellCheck={false}
                  />
                </label>
                <label style={S.fieldLabel}>
                  step path
                  <input
                    style={S.textInput}
                    value={stepPath}
                    onChange={(e) => setStepPath(e.target.value)}
                    placeholder="/path/to/aircraft.step (dev)"
                    spellCheck={false}
                  />
                </label>
              </div>
            </details>
            <button
              style={S.primaryButton}
              onClick={handleSegment}
              disabled={
                segState.status === "running" ||
                exportingDesignId !== null ||
                (!assemblyId.trim() && !stepPath.trim())
              }
            >
              {segState.status === "running" ? "Segmenting..." : "Segment lifting surfaces"}
            </button>
            {segState.status === "error" ? <span style={S.errorText}>{segState.error}</span> : null}
          </div>

          {/* --- nTop mode: everything below Source is the nTop panel; the
                 segment / design / variants / actions pipeline hides. -------- */}
          {ntopMode && ntopNotebook && ntopStatus ? (
            <NtopParametricPanel
              palette={palette}
              projectId={projectId}
              notebook={ntopNotebook}
              engine={ntopStatus.engine}
              frameValue={ntopFrameValue}
              onFrameValueChange={(v) => patchSession({ ntopFrameValue: v })}
              framesLoaded={ntopFramesLoaded}
              framesTotal={ntopFrameList.length}
              framesFailed={Object.keys(ntopFrameErrors).length}
              activeFrame={activeNtopFrame}
              bake={ntopBake}
              onBake={handleNtopBake}
              onCancelBake={handleNtopCancelBake}
              sliceEta={ntopSliceEta}
              onSliceEtaChange={(v) => patchSession({ ntopSliceEta: v })}
              onOpenView={onOpenView}
            />
          ) : (
            <>
          {/* --- b. Surfaces ------------------------------------------------ */}
          {surfaces.length > 0 ? (
            <>
              <div style={S.sectionHead}>Lifting surfaces</div>
              <div style={S.section}>
                {surfaces.map((s) => {
                  const key = surfaceKey(s);
                  const isActive = selected ? key === surfaceKey(selected) : false;
                  const [sw, ch] =
                    s.span_axis === "y"
                      ? [s.bbox[4] - s.bbox[1], s.bbox[3] - s.bbox[0]]
                      : [s.bbox[5] - s.bbox[2], s.bbox[3] - s.bbox[0]];
                  return (
                    <button
                      key={key}
                      style={{ ...S.surfaceRow, ...(isActive ? S.surfaceRowActive : null) }}
                      onClick={() => pickSurface(s)}
                    >
                      <span style={{ fontWeight: 600 }}>{s.label}</span>
                      <span style={S.rowMeta}>
                        span {s.span_axis} · {sw.toFixed(1)} x {ch.toFixed(1)}
                        {typeof s.confidence === "number" ? ` · ${(s.confidence * 100).toFixed(0)}%` : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}

          {/* --- c-e. Editor + live section -------------------------------- */}
          {selected ? (
            <>
              <div style={S.sectionHead}>Design - {selected.label}</div>
              <div style={S.section}>
                <div style={{ display: "flex", gap: 6 }}>
                  <label style={{ ...S.fieldLabel, flex: 1 }}>
                    name
                    <input style={S.textInput} value={csName} onChange={(e) => setCsName(e.target.value)} spellCheck={false} />
                  </label>
                  <label style={{ ...S.fieldLabel, width: 110 }}>
                    type
                    <select style={S.textInput} value={csType} onChange={(e) => setCsType(e.target.value)}>
                      {(CS_TYPES.includes(csType) ? CS_TYPES : [csType, ...CS_TYPES]).map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </label>
                  {isStlSource ? (
                    <label style={{ ...S.fieldLabel, width: 96 }} title="STL files carry no units - pick the scale the mesh was authored in. The mm sliders are converted through it.">
                      stl units
                      <select style={S.textInput} value={stlUnits} onChange={(e) => setStlUnits(e.target.value as "auto" | "mm" | "ft" | "m")}>
                        <option value="auto">auto ({unitMm === 304.8 ? "ft" : unitMm === 1000 ? "m" : "mm"})</option>
                        <option value="ft">feet</option>
                        <option value="m">metres</option>
                        <option value="mm">mm</option>
                      </select>
                    </label>
                  ) : null}
                </div>
                <ParamSlider label="span start" value={params.eta1} min={0} max={0.98} step={0.02} display={params.eta1.toFixed(2)} onChange={(v) => setParam({ eta1: v })} palette={palette} />
                <ParamSlider label="span end" value={params.eta2} min={0.02} max={1} step={0.02} display={params.eta2.toFixed(2)} onChange={(v) => setParam({ eta2: v })} palette={palette} />
                <ParamSlider label="hinge x/c" value={params.xc} min={0.5} max={0.9} step={0.01} display={params.xc.toFixed(2)} onChange={(v) => setParam({ xc: v })} palette={palette} />
                <ParamSlider label="cove gap" value={params.gapMm} min={0.5} max={40} step={0.5} display={`${params.gapMm.toFixed(1)} mm`} onChange={(v) => setParam({ gapMm: v })} palette={palette} />
                <ParamSlider label="cove fillet" value={params.filletMm} min={0} max={12} step={0.5} display={`${params.filletMm.toFixed(1)} mm`} onChange={(v) => setParam({ filletMm: v })} palette={palette} />
                <ParamSlider label="pivot yh" value={params.yh} min={0.1} max={0.9} step={0.05} display={params.yh.toFixed(2)} onChange={(v) => setParam({ yh: v })} palette={palette} />
                <ParamSlider label="deflection" value={params.deflectionDeg} min={-25} max={25} step={1} display={`${params.deflectionDeg.toFixed(0)} deg`} onChange={(v) => setParam({ deflectionDeg: v })} palette={palette} />
                {mirrorable ? (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: palette.ink3 }}>
                    <input type="checkbox" checked={params.mirror} onChange={(e) => setParam({ mirror: e.target.checked })} />
                    mirror onto the opposite semispan
                  </label>
                ) : null}
                {sectionsError ? <span style={S.errorText}>Sections failed - {sectionsError}</span> : null}
              </div>

              <div style={S.sectionHead}>Live section</div>
              <div style={S.section}>
                <ControlSurfaceSectionPanel
                  palette={palette}
                  shape={shapes?.mid ?? null}
                  loading={sectionsPending}
                  sectionEta={sectionEta}
                  onSectionEtaChange={setSectionEta}
                  absoluteEta={absoluteEta}
                  deflectionDeg={params.deflectionDeg}
                  upSign={upSign}
                />
              </div>

              {/* --- f. Variants -------------------------------------------- */}
              <div style={S.sectionHead}>Variants</div>
              <div style={S.section}>
                <button style={S.secondaryButton} onClick={handleSaveVariant}>
                  Save variant
                </button>
                {variantNote ? <span style={S.noteText}>{variantNote}</span> : null}
                {variants.length > 0 ? (
                  variants.map((v) => (
                    <button key={v.nodePath} style={S.surfaceRow} onClick={() => loadVariant(v)} title={v.nodePath}>
                      <span style={{ fontWeight: 600 }}>{v.name}</span>
                      <span style={S.rowMeta}>
                        {v.surface} · {v.csType}
                      </span>
                    </button>
                  ))
                ) : (
                  <span style={S.noteText}>No saved variants yet.</span>
                )}
              </div>

              {/* --- g+h. Actions ------------------------------------------- */}
              <div style={S.sectionHead}>Actions</div>
              <div style={{ ...S.section, paddingBottom: 16 }}>
                <button style={S.primaryButton} onClick={handleSolve} disabled={!shapes?.mid}>
                  Solve in 2D Workbench
                </button>
                {solveNote ? <span style={S.noteText}>{solveNote}</span> : null}
                <button
                  style={S.secondaryButton}
                  onClick={handleBake}
                  disabled={bakeDisabled}
                  data-testid="cs-bake-button"
                >
                  {bake.status === "running" ? "Cutting..." : "Bake true cut"}
                </button>
                {bake.status === "running" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button
                      style={{ ...S.secondaryButton, padding: "2px 8px", fontSize: 11 }}
                      onClick={handleCancelBake}
                      data-testid="cs-bake-cancel"
                    >
                      Cancel
                    </button>
                    {bakeProgressPct !== null ? (
                      <span style={S.footNote} data-testid="cs-bake-progress">
                        {bakeProgressPct}%
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {bakeBlockedReason ? (
                  <span style={S.footNote} data-testid="cs-bake-blocked">
                    {bakeBlockedReason}
                  </span>
                ) : null}
                {bake.status === "running" && bake.message ? (
                  <span style={S.noteText}>{bake.message}</span>
                ) : null}
                {bake.status === "done" ? <span style={S.noteText}>{bake.message}</span> : null}
                {bake.status === "unavailable" ? <span style={S.noteText}>{bake.message}</span> : null}
                {bake.status === "cancelled" ? (
                  <span style={S.noteText} data-testid="cs-bake-cancelled">
                    {bake.message}
                  </span>
                ) : null}
                {bake.status === "error" ? <span style={S.errorText}>Cut failed - {bake.message}</span> : null}
                <span style={S.footNote}>
                  Solve sends this deployed station to the 2D Workbench (the high-lift / section
                  editor). Bake asks the backend CAD engine for the real STEP cut - when it lands,
                  the 3D pane switches to the baked mesh (Source | Baked toggle).
                </span>
              </div>
            </>
          ) : surfaces.length > 0 ? (
            <div style={{ ...S.section, paddingTop: 10 }}>
              <span style={S.noteText}>Pick a lifting surface to design its control surface.</span>
            </div>
          ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

